import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { ReportContext } from '../Context';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

function ZonalDocs() {
  const { report } = useContext(ReportContext);
  const [docs, setDocs] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDocs();
  }, []);

  useEffect(() => {
    handleSearch(searchQuery);
  }, [docs, searchQuery]);

  const fetchDocs = async () => {
    try {
      const response = await fetch('/api/zonaldocs?lang=fr');
      const data = await response.json();
      setDocs(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des documents');
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    const lowerQuery = query.toLowerCase();
    const filtered = docs.filter(doc => 
      doc.title.toLowerCase().includes(lowerQuery)
    );
    // Trier par ordre alphabétique du titre
    filtered.sort((a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }));
    setFilteredDocs(filtered);
  };

  const handleDownload = (doc) => {
    // Log du téléchargement du document
    logAction(LOG_ACTIONS.DOCUMENT_DOWNLOAD, report ? formatReportNumber(report.number, report.year) : null, {
      documentId: doc.id,
      documentTitle: doc.title,
      documentExtension: doc.extension
    });
    window.open(`/api/zonaldocs/download/${doc.id}?lang=fr`, '_blank');
  };

  const getIcon = (extension) => {
    const ext = extension?.toLowerCase();
    if (ext === 'pdf') {
      return <PictureAsPdfIcon color="error" />;
    }
    return <DescriptionIcon color="primary" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ p: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Rechercher un document"
          placeholder="Titre du document"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ mb: 1 }}
        />

        {filteredDocs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py:  2}}>
            {docs.length === 0 ? 'Aucun document disponible' : 'Aucun document trouvé'}
          </Typography>
        ) : (
          <List sx={{ py: 0 }}>
            {filteredDocs.map((doc) => (
              <ListItem key={doc.id} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton 
                  onClick={() => handleDownload(doc)}
                  sx={{ 
                    borderRadius: 1,
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getIcon(doc.extension)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={doc.title}
                    secondary={`Format: ${doc.extension?.toUpperCase()}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <DownloadIcon color="action" fontSize="small" />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}

export default ZonalDocs;

